<script setup>
import { isEmpty, isEqual } from 'lodash'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import { CHECKIN_TEMPLATE_ENTITY_KEYS } from '@/utils/entity-keys'

import AppButton from '@/components/ui/AppButton/AppButton'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppSelect from '@/components/ui/AppSelect/AppSelect'

defineOptions({
  name: 'DescriptionFieldTemplatesSelect'
})

const props = defineProps({
  bottomFixedItems: {
    type: Array,
    default: () => []
  },

  itemLabel: {
    type: [String, Function],
    default: 'label'
  },

  itemValue: {
    type: [String, Function],
    default: 'value'
  },

  options: {
    type: Array,
    required: true
  }
})

const selectedTemplate = ref(null)

const emit = defineEmits({
  'select-template': template => template
})

const store = useStore()
const isPluginAdmin = computed(() => store.state.pluginOptions.isPluginAdmin)

const isOnlyBottomFixedItemsExist = computed(() => {
  return (
    !isEmpty(props.bottomFixedItems) &&
    isEqual(
      props.options.map(o => o[props.itemValue]),
      props.bottomFixedItems
    )
  )
})

const isNoOptions = computed(() => isEmpty(props.options))

const isDisabled = computed(() => isNoOptions.value && !isPluginAdmin.value)
</script>

<template>
  <span
    v-tippy="{
      content: isDisabled ? $t('checkins.no_templates_to_choose') : null,
      maxWidth: 'unset'
    }"
  >
    <AppSelect
      :bottom-fixed-items
      :disabled="isDisabled"
      :dropdown-min-width="200"
      :item-label
      :item-value
      :model-value="selectedTemplate"
      :offset="[0, 0]"
      :options
      :show-no-options-message="false"
      :split-bottom-fixed-items="!isOnlyBottomFixedItemsExist"
      :type="SELECT_TYPES.SECONDARY_NEXT"
      no-options-text-without-search="schedule.no_templates_yet"
      position="bottom-end"
      @update:model-value="emit('select-template', $event)"
    >
      <template #button>
        <AppButton icon-after="arrow-down-black" type="secondary">
          {{ $t('action.templates') }}
        </AppButton>
      </template>

      <template #option-label="{ option }">
        <div v-if="option" class="dfts-OptionLabel">
          <div class="dfts-OptionLabel_Icon">
            <AppIcon v-if="option.icon" :icon-name="option.icon" height="24" width="24" />
            <div v-else-if="option[CHECKIN_TEMPLATE_ENTITY_KEYS.EMOJI]" class="dfts-OptionEmoji">
              {{ option[CHECKIN_TEMPLATE_ENTITY_KEYS.EMOJI] }}
            </div>
          </div>

          <span class="oboard-truncated-text">
            {{ option[itemLabel] }}
          </span>
        </div>
      </template>
    </AppSelect>
  </span>
</template>

<style lang="scss" scoped>
.dfts-OptionLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.dfts-OptionLabel_Icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  text-align: center;
  color: $dark-2;
}
</style>
